import React, { useState, useEffect } from 'react'
import { 
  TrendingUp, 
  TrendingDown, 
  Zap, 
  Play, 
  X,
  RefreshCw,
  AlertCircle,
  Clock,
  CheckCircle,
  BarChart3,
  ExternalLink
} from 'lucide-react'
import { signalsAPI } from '../services/api'
import { portfolioAPI } from '../services/api'
import { recommendationsAPI } from '../services/api'
import { stockDataAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import LoadingSpinner from '../components/LoadingSpinner'
import { openTradingView } from '../utils/tradingView'
import toast from 'react-hot-toast'

const Signals = () => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [analysisLog, setAnalysisLog] = useState('')
  const [executionLog, setExecutionLog] = useState('')
  const [signals, setSignals] = useState([])
  const [filter, setFilter] = useState('all')
  const [stats, setStats] = useState(null)

  useEffect(() => {
    if (user?.kiteConnected) {
      fetchSignals()
      fetchStats()
    } else {
      setLoading(false)
    }
  }, [user, filter])

  const fetchSignals = async () => {
    try {
      const response = await signalsAPI.getSignals({ 
        status: filter === 'all' ? undefined : filter.toUpperCase(),
        limit: 50 
      })
      setSignals(response.data)
    } catch (error) {
      console.error('Signals fetch error:', error)
      toast.error('Failed to fetch signals')
    } finally {
      setLoading(false)
    }
  }

  const fetchStats = async () => {
    try {
      const response = await signalsAPI.getStats()
      setStats(response.data)
    } catch (error) {
      console.error('Stats fetch error:', error)
    }
  }

  const generateSignals = async () => {
    setGenerating(true)
    setAnalysisLog('🚀 Initializing signal generation...\n')
    try {
      // 1. Fetch open positions and holdings BEFORE generation
      const [positionsRes, holdingsRes] = await Promise.all([
        portfolioAPI.getPositions(),
        portfolioAPI.getHoldings(),
      ])
      const openSymbols = new Set([
        ...((positionsRes.data?.data?.net || []).map(p => p.tradingsymbol)),
        ...((holdingsRes.data?.data || []).map(h => h.tradingsymbol))
      ])

      // 2. Fetch recommendations from Recommendation API
      setAnalysisLog(prev => prev + '📡 Fetching latest recommendations...\n')
      const recRes = await recommendationsAPI.getIntradayRecommendations()
      const recommendations = recRes.data?.recommendations || []
      if (!recommendations.length) {
        setAnalysisLog(prev => prev + '❌ No recommendations found.\n')
        toast.error('No recommendations found')
        setGenerating(false)
        return
      }
      setAnalysisLog(prev => prev + `✅ Got ${recommendations.length} recommended stocks.\n`)
      
      // 2.1. Fetch real-time stock data for recommendations
      const stockDataResults = {}
      for (const rec of recommendations) {
        const symbol = rec.Symbol
        try {
          const response = await stockDataAPI.getOHLC(symbol)
          if (response.data.status && response.data.data.fetched.length > 0) {
            stockDataResults[symbol] = response.data.data.fetched[0]
          }
          await new Promise(resolve => setTimeout(resolve, 300)) // rate limit
        } catch (err) {
          // ignore
        }
      }

      // 2.2. Merge recommendation and stock data into the expected format
      const recommendationStocks = recommendations.map(rec => {
        const symbol = rec.Symbol
        const stock = stockDataResults[symbol] || {}
        return {
          symbol,
          ltp: stock.ltp ?? rec.EntryPrice,
          open: stock.open ?? null,
          high: stock.high ?? null,
          low: stock.low ?? null,
          close: stock.close ?? null,
          netChange: stock.netChange ?? null,
          percentChange: stock.netChangePercent ?? null,
          volume: stock.tradeVolume ?? null,
          // Optionally include recommendation fields for AI context
          signal: rec.Signal,
          confidence: rec.Confidence,
          entryPrice: rec.EntryPrice,
          targetPrice: rec.TargetPrice,
          stopLoss: rec.StopLoss,
          strategy: rec.Strategy,
          reasoning: rec.Reasoning,
        }
      })

      // Log the stocks being sent for analysis
      setAnalysisLog(prev => prev + `\n🟢 Sending the following stocks to backend for analysis:\n`)
      recommendationStocks.forEach(stock => {
        setAnalysisLog(prev => prev + `   • ${stock.symbol}: LTP ₹${stock.ltp}\n`)
      })

      // 3. Generate signals using recommendations
      const response = await signalsAPI.generateSignals({ recommendationStocks })
      if (response.data.analysisLog) {
        setAnalysisLog(response.data.analysisLog + '\n\n🎉 Process completed successfully!')
      } else {
        setAnalysisLog(prev => prev + 'Analysis complete!\n')
        setAnalysisLog(prev => prev + `${response.data.message}\n`)
      }
      toast.success(response.data.message)

      // 4. Wait for backend to finish saving signals
      await new Promise(resolve => setTimeout(resolve, 1000))

      // 5. Fetch latest signals from backend
      const backendSignalsRes = await signalsAPI.getSignals({ status: 'ACTIVE', limit: 100 })
      const backendSignals = backendSignalsRes.data || []
      const generatedSymbols = new Set((response.data.signals || []).map(s => s.symbol))

      // 6. For each generated symbol, find the corresponding ACTIVE signal and execute if not in open/executed positions
      for (const signal of backendSignals) {
        if (generatedSymbols.has(signal.symbol) && !openSymbols.has(signal.symbol)) {
          // eslint-disable-next-line no-await-in-loop
          await executeSignal(signal._id)
        }
      }

      fetchSignals()
      fetchStats()
    } catch (error) {
      console.error('Generate signals error:', error)
      setAnalysisLog(prev => prev + `\n❌ Error: ${error.response?.data?.message || error.message}\n`)
      toast.error('Failed to generate signals')
    } finally {
      setGenerating(false)
    }
  }

  const executeSignal = async (signalId) => {
    try {
      const response = await signalsAPI.executeSignal(signalId)
      
      // Add execution log
      if (response.data.executionLog) {
        setExecutionLog(prev => prev + '\n' + '='.repeat(50) + '\n')
        setExecutionLog(prev => prev + `Order Execution - ${new Date().toLocaleString()}\n`)
        setExecutionLog(prev => prev + '='.repeat(50) + '\n')
        setExecutionLog(prev => prev + response.data.executionLog + '\n\n')
      }
      
      toast.success(`Order placed: ${response.data.message}`)
      fetchSignals()
    } catch (error) {
      console.error('Execute signal error:', error)
      
      // Add error to execution log
      if (error.response?.data?.executionLog) {
        setExecutionLog(prev => prev + '\n' + '='.repeat(50) + '\n')
        setExecutionLog(prev => prev + `Order Execution Failed - ${new Date().toLocaleString()}\n`)
        setExecutionLog(prev => prev + '='.repeat(50) + '\n')
        setExecutionLog(prev => prev + error.response.data.executionLog + '\n\n')
      }
      
      toast.error(error.response?.data?.message || 'Failed to execute signal')
    }
  }

  const cancelOrder = async (signalId) => {
    try {
      const response = await signalsAPI.cancelOrder(signalId)
      
      // Add cancellation log
      if (response.data.executionLog) {
        setExecutionLog(prev => prev + '\n' + '='.repeat(50) + '\n')
        setExecutionLog(prev => prev + `Order Cancellation - ${new Date().toLocaleString()}\n`)
        setExecutionLog(prev => prev + '='.repeat(50) + '\n')
        setExecutionLog(prev => prev + response.data.executionLog + '\n\n')
      }
      
      toast.success('Order cancelled successfully')
      fetchSignals()
    } catch (error) {
      console.error('Cancel order error:', error)
      
      // Add error to execution log
      if (error.response?.data?.executionLog) {
        setExecutionLog(prev => prev + '\n' + '='.repeat(50) + '\n')
        setExecutionLog(prev => prev + `Order Cancellation Failed - ${new Date().toLocaleString()}\n`)
        setExecutionLog(prev => prev + '='.repeat(50) + '\n')
        setExecutionLog(prev => prev + error.response.data.executionLog + '\n\n')
      }
      
      toast.error(error.response?.data?.message || 'Failed to cancel order')
    }
  }

  const cancelSignal = async (signalId) => {
    try {
      await signalsAPI.cancelSignal(signalId)
      toast.success('Signal cancelled')
      fetchSignals()
    } catch (error) {
      console.error('Cancel signal error:', error)
      toast.error('Failed to cancel signal')
    }
  }

  const getSignalIcon = (signalType) => {
    return signalType === 'BUY' ? (
      <TrendingUp className="w-5 h-5 text-success-600" />
    ) : (
      <TrendingDown className="w-5 h-5 text-danger-600" />
    )
  }

  const getStatusBadge = (status) => {
    const badges = {
      ACTIVE: 'badge-info',
      EXECUTED: 'badge-success',
      EXPIRED: 'badge-warning',
      CANCELLED: 'badge-danger'
    }
    return badges[status] || 'badge-info'
  }

  const getStrategyName = (strategy) => {
    const names = {
      MA_CROSSOVER: 'Moving Average Crossover',
      RSI: 'RSI Strategy',
      VOLUME_SPIKE: 'Volume Spike'
    }
    return names[strategy] || strategy
  }

  if (!user?.kiteConnected) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Kite Not Connected</h2>
        <p className="text-gray-600 mb-6">
          Please connect your Kite account in Settings to view trading signals.
        </p>
        <a href="/settings" className="btn-primary">
          Go to Settings
        </a>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Trading Signals</h1>
        <div className="flex items-center space-x-3">
          <div className="text-sm text-gray-600">
            Powered by Google Gemini AI
          </div>
          <button
            onClick={fetchSignals}
            className="btn-secondary flex items-center space-x-2"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          <button
            onClick={generateSignals}
            disabled={generating}
            className="btn-primary flex items-center space-x-2"
          >
            {generating ? (
              <LoadingSpinner size="small" />
            ) : (
              <Zap className="w-4 h-4" />
            )}
            <span>Generate Signals</span>
          </button>
        </div>
      </div>

      {/* Analysis Log */}
      {analysisLog && (
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-900">Analysis Log</h3>
            <button
              onClick={() => setAnalysisLog('')}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear
            </button>
          </div>
          <textarea
            value={analysisLog}
            readOnly
            rows={15}
            className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 font-mono text-sm resize-none"
            placeholder="Analysis log will appear here..."
          />
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {stats.statusStats.map((stat) => (
            <div key={stat._id} className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat._id} Signals</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.count}</p>
                </div>
                <div className="text-sm text-gray-500">
                  {stat.avgConfidence.toFixed(1)}% avg confidence
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filter Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {['all', 'active', 'executed', 'expired', 'execution-log'].map((status) => (
            <button
              key={status}
              onClick={() => {
                setFilter(status)
              }}
              className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${
                filter === status
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {status === 'execution-log' ? 'Execution Log' : status}
            </button>
          ))}
        </nav>
      </div>

      {/* Execution Log Tab */}
      {filter === 'execution-log' && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Order Execution Log</h3>
            <button
              onClick={() => setExecutionLog('')}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear Log
            </button>
          </div>
          
          {executionLog ? (
            <textarea
              value={executionLog}
              readOnly
              rows={20}
              className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 font-mono text-sm resize-none"
              placeholder="Order execution logs will appear here..."
            />
          ) : (
            <div className="text-center py-8 text-gray-500">
              <BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No execution logs yet</p>
              <p className="text-sm">Execute signals to see detailed logs here</p>
            </div>
          )}
        </div>
      )}

      {/* Signals List */}
      {filter !== 'execution-log' && signals.length === 0 ? (
        <div className="text-center py-12">
          <TrendingUp className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No AI Signals Found</h2>
          <p className="text-gray-600 mb-6">
            Generate AI-powered trading signals using Google Gemini and your watchlist data
          </p>
          <button
            onClick={generateSignals}
            disabled={generating}
            className="btn-primary flex items-center space-x-2 mx-auto"
          >
            {generating ? (
              <LoadingSpinner size="small" />
            ) : (
              <Zap className="w-4 h-4" />
            )}
            <span>Generate AI Signals</span>
          </button>
        </div>
      ) : (
        filter !== 'execution-log' && (
          <div className="space-y-4">
          {signals.map((signal) => (
            <div key={signal._id} className="card">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    {getSignalIcon(signal.signalType)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {signal.symbol}
                        </h3>
                        <button
                          onClick={() => openTradingView(signal.symbol, signal.exchange)}
                          className="text-blue-600 hover:text-blue-800"
                          title="View on TradingView"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </button>
                      </div>
                      <span className={`badge ${
                        signal.signalType === 'BUY' ? 'badge-success' : 'badge-danger'
                      }`}>
                        {signal.signalType}
                      </span>
                      <span className={`badge ${getStatusBadge(signal.status)}`}>
                        {signal.status}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                      <div>
                        <p className="text-sm text-gray-600">Strategy</p>
                        <p className="font-medium">
                          {signal.strategy === 'GEMINI_AI' ? 'Gemini AI Analysis' : getStrategyName(signal.strategy)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Price</p>
                        <p className="font-medium">₹{signal.price.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Confidence</p>
                        <p className="font-medium">{signal.confidence}%</p>
                      </div>
                    </div>

                    {signal.targetPrice && signal.stopLoss && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                        <div>
                          <p className="text-sm text-gray-600">Target</p>
                          <p className="font-medium text-success-600">₹{signal.targetPrice.toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Stop Loss</p>
                          <p className="font-medium text-danger-600">₹{signal.stopLoss.toFixed(2)}</p>
                        </div>
                      </div>
                    )}

                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-sm text-gray-700">{signal.reasoning}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {signal.status === 'ACTIVE' && (
                    <>
                      <button
                        onClick={() => executeSignal(signal._id)}
                        className="btn-success flex items-center space-x-1"
                      >
                        <Play className="w-4 h-4" />
                        <span>Execute</span>
                      </button>
                      <button
                        onClick={() => cancelSignal(signal._id)}
                        className="btn-danger flex items-center space-x-1"
                      >
                        <X className="w-4 h-4" />
                        <span>Cancel</span>
                      </button>
                    </>
                  )}
                  
                  {signal.status === 'EXECUTED' && signal.orderId && signal.orderStatus !== 'CANCELLED' && (
                    <button
                      onClick={() => cancelOrder(signal._id)}
                      className="btn-danger flex items-center space-x-1"
                    >
                      <X className="w-4 h-4" />
                      <span>Cancel Order</span>
                    </button>
                  )}
                  
                  <div className="text-right text-sm text-gray-500">
                    <div className="flex items-center space-x-1">
                      <Clock className="w-4 h-4" />
                      <span>{new Date(signal.createdAt).toLocaleDateString()}</span>
                    </div>
                    {signal.executedAt && (
                      <div className="flex items-center space-x-1 mt-1">
                        <CheckCircle className="w-4 h-4" />
                        <span>{new Date(signal.executedAt).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        )
      )}
    </div>
  )
}

export default Signals